import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Refresh as RefreshIcon, Save as SaveIcon } from '@mui/icons-material';
import type { SubscriptionOffer } from '@/content/subscriptionPricing';
import {
  earlyBirdPrice,
  formatCatalogYearly,
  formatUsdYearly,
  PLAYER_INCLUDED_SPORTS,
} from '@/content/subscriptionPricing';
import {
  DEFAULT_COUNTRY_RATES,
  DEFAULT_SUBSCRIPTION_COUNTRY_CODE,
  PLAYER_FAMILY_CATALOG,
  normalizePlayerFamilyCatalog,
  resolvePlanEnabledByCategory,
  type PlayerFamilyCatalog,
  type PlayerPlanOffering,
  type SubscriptionCountryRate,
} from '@/content/subscriptionCatalog';
import type { PlayerPlanId } from '@types/index';

function getAdminToken(): string | null {
  return localStorage.getItem('accessToken') || localStorage.getItem('token');
}

const PLAN_PRICE_KEYS: PlayerPlanId[] = ['basic', 'multi', 'elite'];

function emptyRate(): SubscriptionCountryRate {
  return {
    code: '',
    name: '',
    currency: 'USD',
    individual: { basic: 0, multi: 0, elite: 0 },
    family: { basic: 0, multi: 0, elite: 0 },
    extraSport: 0,
    extraChild: 0,
  };
}

const AdminSubscriptions: React.FC = () => {
  const [offers, setOffers] = useState<SubscriptionOffer[]>([]);
  const [catalog, setCatalog] = useState<PlayerFamilyCatalog>(PLAYER_FAMILY_CATALOG);
  const [countryRates, setCountryRates] = useState<SubscriptionCountryRate[]>(DEFAULT_COUNTRY_RATES);
  const [defaultCountryCode, setDefaultCountryCode] = useState(DEFAULT_SUBSCRIPTION_COUNTRY_CODE);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = getAdminToken();
      const headers: Record<string, string> = {};
      if (token) headers.Authorization = `Bearer ${token}`;
      const [offersRes, catalogRes] = await Promise.all([
        fetch('/api/admin/subscription-offers', { headers }),
        fetch('/api/admin/subscription-catalog', { headers }),
      ]);
      const offersData = await offersRes.json();
      if (!offersRes.ok || !offersData.success) throw new Error(offersData.message || 'Failed to load offers');
      setOffers(offersData.data || []);

      const catalogData = await catalogRes.json();
      if (catalogRes.ok && catalogData.success && catalogData.data) {
        setCatalog(normalizePlayerFamilyCatalog(catalogData.data.catalog));
        setCountryRates(
          Array.isArray(catalogData.data.countryRates) && catalogData.data.countryRates.length
            ? catalogData.data.countryRates
            : DEFAULT_COUNTRY_RATES,
        );
        setDefaultCountryCode(catalogData.data.defaultCountryCode || DEFAULT_SUBSCRIPTION_COUNTRY_CODE);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load offers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const updateLocal = (id: string, patch: Partial<SubscriptionOffer>) => {
    setOffers((prev) => prev.map((o) => (o.id === id ? { ...o, ...patch } : o)));
  };

  const save = async (offer: SubscriptionOffer) => {
    setSavingId(offer.id);
    setError(null);
    setSuccess(null);
    try {
      const token = getAdminToken();
      const res = await fetch(`/api/admin/subscription-offers/${offer.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(offer),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Save failed');
      setOffers((prev) => prev.map((o) => (o.id === offer.id ? data.data : o)));
      setSuccess(data.message || `Saved ${offer.name}`);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSavingId(null);
    }
  };

  const updatePlan = (id: PlayerPlanId, patch: Partial<PlayerPlanOffering>) => {
    setCatalog((prev) => ({
      ...prev,
      plans: prev.plans.map((plan) => (plan.id === id ? { ...plan, ...patch } : plan)),
    }));
  };

  const updateRate = (index: number, patch: Partial<SubscriptionCountryRate>) => {
    setCountryRates((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const updateRatePrice = (
    index: number,
    category: 'individual' | 'family',
    planId: PlayerPlanId,
    value: number,
  ) => {
    setCountryRates((prev) =>
      prev.map((row, i) =>
        i === index ? { ...row, [category]: { ...row[category], [planId]: value } } : row,
      ),
    );
  };

  const saveFamilyCatalog = async () => {
    setSavingId('family');
    setError(null);
    setSuccess(null);
    try {
      const token = getAdminToken();
      const res = await fetch('/api/admin/subscription-catalog', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          catalog,
          countryRates,
          defaultCountryCode,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Save failed');
      if (data.data?.catalog) setCatalog(normalizePlayerFamilyCatalog(data.data.catalog));
      if (Array.isArray(data.data?.countryRates)) setCountryRates(data.data.countryRates);
      if (data.data?.defaultCountryCode) setDefaultCountryCode(data.data.defaultCountryCode);
      setSuccess(data.message || 'Saved player plans and country rates');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSavingId(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap spacing={1}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800 }}>
            Subscriptions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Player/family yearly plans and country rates, plus public marketing offers. Dashboard quotes use the
            player&apos;s country (or the default country below).
          </Typography>
        </Box>
        <Button startIcon={<RefreshIcon />} onClick={() => void load()} sx={{ textTransform: 'none' }}>
          Refresh
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Stack spacing={2.5}>
        <Box sx={{ p: 2.5, borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff' }}>
          <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', mb: 0.5 }}>Player & family plans</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Same yearly price for every sport. Single includes 1 sport; Multi-sport and Elite include up to 3.
            Public profile is part of every plan. Disable a plan to keep it visible but greyed (no price, no
            Purchase). Pre-book still works and upgrades onto the plan when you enable it.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
            <TextField
              label="Individual → Family discount %"
              type="number"
              value={catalog.individualToFamilyDiscountPercent}
              onChange={(e) =>
                setCatalog((prev) => ({
                  ...prev,
                  individualToFamilyDiscountPercent: Number(e.target.value) || 0,
                }))
              }
              sx={{ width: { sm: 240 } }}
            />
            <TextField
              fullWidth
              label="Headline"
              value={catalog.headline}
              onChange={(e) => setCatalog((prev) => ({ ...prev, headline: e.target.value }))}
            />
          </Stack>
          <TextField
            fullWidth
            label="Terms / disclaimer"
            value={catalog.termsNote}
            onChange={(e) => setCatalog((prev) => ({ ...prev, termsNote: e.target.value }))}
            sx={{ mb: 2 }}
          />
          <Stack spacing={1.5}>
            {catalog.plans.map((plan) => (
              <Box key={plan.id} sx={{ p: 1.75, borderRadius: 1.5, border: '1px solid #e2e8f0' }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} sx={{ mb: 1.25 }}>
                  <TextField
                    label="Plan name"
                    value={plan.name}
                    onChange={(e) => updatePlan(plan.id, { name: e.target.value })}
                    sx={{ minWidth: 140 }}
                  />
                  <TextField
                    fullWidth
                    label="Tagline"
                    value={plan.tagline}
                    onChange={(e) => updatePlan(plan.id, { tagline: e.target.value })}
                  />
                  <TextField
                    label="Included sports"
                    type="number"
                    value={plan.includedSports}
                    onChange={(e) => updatePlan(plan.id, { includedSports: Number(e.target.value) || 1 })}
                    sx={{ width: 140 }}
                  />
                </Stack>
                <Stack direction="row" spacing={2} sx={{ mb: 1.25 }} flexWrap="wrap" useFlexGap>
                  {(['individual', 'family'] as const).map((category) => {
                    const enabledByCategory = resolvePlanEnabledByCategory(plan);
                    return (
                      <FormControlLabel
                        key={category}
                        control={
                          <Switch
                            checked={enabledByCategory[category]}
                            onChange={(e) => {
                              const next = { ...enabledByCategory, [category]: e.target.checked };
                              updatePlan(plan.id, {
                                enabledByCategory: next,
                                enabled: next.individual || next.family,
                              });
                            }}
                          />
                        }
                        label={`${category === 'family' ? 'Family' : 'Individual'} enabled`}
                      />
                    );
                  })}
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(plan.prebook)}
                        onChange={(e) => updatePlan(plan.id, { prebook: e.target.checked })}
                      />
                    }
                    label="Plan-level pre-book"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(plan.beta)}
                        onChange={(e) => updatePlan(plan.id, { beta: e.target.checked })}
                      />
                    }
                    label="Internal launch flag"
                  />
                </Stack>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Features (one per line)"
                  value={(plan.features || []).join('\n')}
                  onChange={(e) =>
                    updatePlan(plan.id, {
                      features: e.target.value
                        .split('\n')
                        .map((s) => s.trim())
                        .filter(Boolean),
                    })
                  }
                />
              </Box>
            ))}
          </Stack>
        </Box>

        <Box sx={{ p: 2.5, borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5} sx={{ mb: 1.5 }}>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }}>Country rates</Typography>
              <Typography variant="body2" color="text.secondary">
                Yearly prices per country and currency. Unset countries fall back to {defaultCountryCode}.
              </Typography>
            </Box>
            <TextField
              select
              label="Default country"
              value={defaultCountryCode}
              onChange={(e) => setDefaultCountryCode(e.target.value)}
              sx={{ minWidth: 180 }}
              size="small"
            >
              {countryRates.map((row) => (
                <MenuItem key={row.code} value={row.code}>
                  {row.code} · {row.name}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          <Stack spacing={1.5}>
            {countryRates.map((row, index) => (
              <Box key={`${row.code}-${index}`} sx={{ p: 1.5, borderRadius: 1.5, border: '1px solid #e2e8f0' }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }} flexWrap="wrap" useFlexGap>
                  <Chip size="small" label={row.code || 'New'} />
                  {row.code === defaultCountryCode && <Chip size="small" color="success" label="Default" />}
                  <Typography variant="body2" color="text.secondary">
                    Extra sport {formatCatalogYearly(row.extraSport, row.currency)} · extra child{' '}
                    {formatCatalogYearly(row.extraChild, row.currency)}
                  </Typography>
                </Stack>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ mb: 1 }}>
                  <TextField
                    label="Code"
                    value={row.code}
                    onChange={(e) => updateRate(index, { code: e.target.value.toUpperCase() })}
                    sx={{ width: 90 }}
                  />
                  <TextField
                    fullWidth
                    label="Country"
                    value={row.name}
                    onChange={(e) => updateRate(index, { name: e.target.value })}
                  />
                  <TextField
                    label="Currency"
                    value={row.currency}
                    onChange={(e) => updateRate(index, { currency: e.target.value.toUpperCase() })}
                    sx={{ width: 110 }}
                  />
                  <TextField
                    label="Extra sport"
                    type="number"
                    value={row.extraSport}
                    onChange={(e) => updateRate(index, { extraSport: Number(e.target.value) || 0 })}
                    sx={{ width: 130 }}
                  />
                  <TextField
                    label="Extra child"
                    type="number"
                    value={row.extraChild}
                    onChange={(e) => updateRate(index, { extraChild: Number(e.target.value) || 0 })}
                    sx={{ width: 130 }}
                  />
                </Stack>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                  {(['individual', 'family'] as const).map((category) =>
                    PLAN_PRICE_KEYS.map((planId) => (
                      <TextField
                        key={`${category}-${planId}`}
                        label={`${category === 'family' ? 'Fam' : 'Ind'} ${
                          planId === 'basic' ? 'Single' : planId === 'multi' ? 'Multi' : 'Elite'
                        }`}
                        type="number"
                        value={row[category][planId]}
                        onChange={(e) => updateRatePrice(index, category, planId, Number(e.target.value) || 0)}
                      />
                    )),
                  )}
                </Stack>
              </Box>
            ))}
          </Stack>
          <Button
            sx={{ mt: 1.5, textTransform: 'none' }}
            onClick={() => setCountryRates((prev) => [...prev, emptyRate()])}
          >
            Add country
          </Button>
          <Button
            variant="contained"
            startIcon={savingId === 'family' ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            disabled={savingId === 'family'}
            onClick={() => void saveFamilyCatalog()}
            sx={{ display: 'block', mt: 2, textTransform: 'none', fontWeight: 700 }}
          >
            Save plans & country rates
          </Button>
        </Box>

        <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', pt: 1 }}>Public marketing offers</Typography>
        {offers.map((offer) => {
          const payable =
            !offer.customPricing && offer.yearlyUsd != null && offer.earlyBirdEnabled
              ? earlyBirdPrice(offer.yearlyUsd, offer.earlyBirdPercent)
              : offer.yearlyUsd;
          return (
            <Box
              key={offer.id}
              sx={{
                p: 2.5,
                borderRadius: 2,
                border: '1px solid #e2e8f0',
                bgcolor: '#fff',
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }}>{offer.name}</Typography>
                <Chip size="small" label={offer.id} />
                {offer.highlight && <Chip size="small" color="warning" label="Highlighted" />}
                {!offer.customPricing && offer.yearlyUsd != null && (
                  <Chip
                    size="small"
                    color="success"
                    label={
                      offer.earlyBirdEnabled
                        ? `${formatUsdYearly(payable || 0)} early bird · list ${formatUsdYearly(offer.yearlyUsd)}`
                        : `${formatUsdYearly(offer.yearlyUsd)} / year`
                    }
                  />
                )}
                {offer.customPricing && <Chip size="small" label="Custom / contact us" />}
              </Stack>

              <Stack spacing={1.5}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                  <TextField
                    fullWidth
                    label="Display name"
                    value={offer.name}
                    onChange={(e) => updateLocal(offer.id, { name: e.target.value })}
                  />
                  <TextField
                    fullWidth
                    label="Audience"
                    value={offer.audience}
                    onChange={(e) => updateLocal(offer.id, { audience: e.target.value })}
                  />
                </Stack>

                <TextField
                  fullWidth
                  label="Offer headline"
                  value={offer.offerHeadline || ''}
                  onChange={(e) => updateLocal(offer.id, { offerHeadline: e.target.value })}
                  helperText="Shown on subscriptions / homepage (e.g. Improve Foundation Skills…)"
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={offer.customPricing}
                      onChange={(e) =>
                        updateLocal(offer.id, {
                          customPricing: e.target.checked,
                          yearlyUsd: e.target.checked ? null : offer.yearlyUsd ?? 200,
                        })
                      }
                    />
                  }
                  label="Custom pricing (contact us — no fixed list price)"
                />

                {!offer.customPricing && (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                    <TextField
                      label="Yearly list price (USD)"
                      type="number"
                      value={offer.yearlyUsd ?? ''}
                      onChange={(e) =>
                        updateLocal(offer.id, {
                          yearlyUsd: e.target.value === '' ? null : Number(e.target.value),
                        })
                      }
                      sx={{ minWidth: 180 }}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={offer.earlyBirdEnabled}
                          onChange={(e) => updateLocal(offer.id, { earlyBirdEnabled: e.target.checked })}
                        />
                      }
                      label="Early bird enabled"
                    />
                    <TextField
                      label="Early bird %"
                      type="number"
                      disabled={!offer.earlyBirdEnabled}
                      value={offer.earlyBirdPercent}
                      onChange={(e) =>
                        updateLocal(offer.id, { earlyBirdPercent: Number(e.target.value) || 0 })
                      }
                      sx={{ width: 140 }}
                    />
                  </Stack>
                )}

                {offer.customPricing && (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={offer.earlyBirdEnabled}
                          onChange={(e) => updateLocal(offer.id, { earlyBirdEnabled: e.target.checked })}
                        />
                      }
                      label="Early bird enabled"
                    />
                    <TextField
                      label="Early bird %"
                      type="number"
                      disabled={!offer.earlyBirdEnabled}
                      value={offer.earlyBirdPercent}
                      onChange={(e) =>
                        updateLocal(offer.id, { earlyBirdPercent: Number(e.target.value) || 0 })
                      }
                      sx={{ width: 140 }}
                    />
                    <TextField
                      label="Early bird limit (first N)"
                      type="number"
                      disabled={!offer.earlyBirdEnabled}
                      value={offer.earlyBirdLimit ?? ''}
                      onChange={(e) =>
                        updateLocal(offer.id, {
                          earlyBirdLimit: e.target.value === '' ? null : Number(e.target.value),
                        })
                      }
                      sx={{ width: 180 }}
                      helperText={
                        offer.earlyBirdRemaining != null
                          ? `${offer.earlyBirdRemaining} slots remaining`
                          : 'e.g. 200 academies / 50 schools'
                      }
                    />
                    <TextField
                      label="Claimed"
                      type="number"
                      value={offer.earlyBirdClaimed ?? 0}
                      onChange={(e) =>
                        updateLocal(offer.id, { earlyBirdClaimed: Number(e.target.value) || 0 })
                      }
                      sx={{ width: 120 }}
                    />
                  </Stack>
                )}

                <TextField
                  fullWidth
                  label={`Included sports (comma-separated — default ${PLAYER_INCLUDED_SPORTS.join(', ')})`}
                  value={(offer.sports || []).join(', ')}
                  onChange={(e) =>
                    updateLocal(offer.id, {
                      sports: e.target.value
                        .split(',')
                        .map((s) => s.trim())
                        .filter(Boolean),
                    })
                  }
                />

                <TextField
                  fullWidth
                  multiline
                  minRows={4}
                  label="Features (one per line)"
                  value={(offer.features || []).join('\n')}
                  onChange={(e) =>
                    updateLocal(offer.id, {
                      features: e.target.value
                        .split('\n')
                        .map((s) => s.trim())
                        .filter(Boolean),
                    })
                  }
                />

                <TextField
                  fullWidth
                  label="Includes note"
                  value={offer.includesNote || ''}
                  onChange={(e) => updateLocal(offer.id, { includesNote: e.target.value })}
                />
                <TextField
                  fullWidth
                  label="Terms / disclaimer"
                  value={offer.termsNote || ''}
                  onChange={(e) => updateLocal(offer.id, { termsNote: e.target.value })}
                  helperText="e.g. *Terms & conditions apply."
                />

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <TextField
                    fullWidth
                    label="CTA label"
                    value={offer.ctaLabel}
                    onChange={(e) => updateLocal(offer.id, { ctaLabel: e.target.value })}
                  />
                  <TextField
                    fullWidth
                    label="CTA path"
                    value={offer.ctaPath}
                    onChange={(e) => updateLocal(offer.id, { ctaPath: e.target.value })}
                  />
                </Stack>

                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(offer.highlight)}
                      onChange={(e) => updateLocal(offer.id, { highlight: e.target.checked })}
                    />
                  }
                  label="Highlight on public subscriptions page"
                />

                <Button
                  variant="contained"
                  startIcon={savingId === offer.id ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                  disabled={savingId === offer.id}
                  onClick={() => void save(offer)}
                  sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 700 }}
                >
                  Save {offer.name}
                </Button>
              </Stack>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};

export default AdminSubscriptions;
